/** 投放项目双视图，沿既有 App 卡片与列表模式；切换视图保留搜索、分页和选择。 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  type ProjectSummary,
  landingProjectQueryKey,
  useLandingProductsPage,
  useLandingProjectExecutionQueueCount,
  useLandingProjectLanguageOptions,
  useLandingProjects,
} from '~/data/queries';
import {
  type ProjectDesensitizationFilter,
  type ProjectSort,
  landingProjectsApi,
} from '~/hooks/use-landing-projects';
import { bookCodeDisplay } from '~/lib/book-identity';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { BookCover } from '~/ui/BookCover';
import { CopyableCode } from '~/ui/CopyableCode';
import { ConfirmDialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { LangChip } from '~/ui/LangChip';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { CardGridSkeleton, TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { LandingExecutionQueueDrawer } from './LandingExecutionQueueDrawer';
import { LandingProjectBookVisibilityWarnings } from './LandingProjectBookVisibilityWarnings';
import { LandingProjectEditor } from './LandingProjectEditor';
import {
  LandingProjectQuickDesensitizeDialog,
  canQuickDesensitizeRole,
  quickDesensitizeBlockedReason,
  quickDesensitizeProjectBlockedReason,
} from './LandingProjectQuickDesensitizeDialog';
import { ProjectProgressSummary, ProjectState } from './landing-project-display';
import { useLandingProjectListQuery } from './landing-project-list-url-query';
/** 按已加入投放章节的 AI 脱敏状态筛选（AE §11.1.2）：未脱敏 / 失败为至少一章命中，成功为全部完成。 */
const DESENSITIZATION_FILTER_OPTIONS: { value: ProjectDesensitizationFilter; label: string }[] = [
  { value: 'NOT_PROCESSED', label: '未脱敏' },
  { value: 'FAILED', label: '脱敏失败' },
  { value: 'SUCCEEDED', label: '脱敏成功' },
];
/** 勾选快照：项目行及其所来自的列表数据时间（dataUpdatedAt）。 */
interface SelectedProject {
  row: ProjectSummary;
  at: number;
}
/**
 * 候选行是否比已勾选快照更新：先比服务端 updated_at（归档 / 恢复、改章节等写操作会推进，不受请求先发后到影响），
 * 相同再比列表数据时间（书籍资格等派生字段变化不推进 updated_at）。客户端时间只能近似读库先后：跨筛选并发请求的
 * 秒级窗口内书籍资格恰好变化时可能取到旧值，以服务端提交时的资格校验为准（拒收会明确提示）。
 */
function isNewerSelectedProject(candidate: SelectedProject, current: SelectedProject) {
  const candidateUpdatedAt = Date.parse(candidate.row.updated_at);
  const currentUpdatedAt = Date.parse(current.row.updated_at);
  if (candidateUpdatedAt !== currentUpdatedAt) return candidateUpdatedAt > currentUpdatedAt;
  return candidate.at > current.at;
}

/**
 * error_summary 取自最近一次执行的 error；该执行仍在进行（active_execution 即同一执行）时，
 * 进度摘要已按子项状态展示原因（重试中弱化、终态失败红字），这里再红字重复只会误报。
 */
function errorSummaryShownInProgress(row: ProjectSummary) {
  return !!row.active_execution;
}
function BookIdentity({ row }: { row: ProjectSummary }) {
  return (
    <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
      <BookCover src={row.novel.cover_url} alt={`${row.novel.cn_title}封面`} width={52} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
          {row.novel.cn_title || row.novel.title}
        </div>
        <div className="hstack" style={{ gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          <span className="mono">
            {bookCodeDisplay({
              bookCode: row.novel.book_code,
              bookGroupCode: row.novel.book_group_code,
              locale: row.novel.locale,
            })}
          </span>
          <LangChip code={row.novel.locale} />
        </div>
      </div>
    </div>
  );
}
export function LandingProjectsPanel({ onOpen }: { onOpen: (id: string) => void }) {
  const userId = useAuthStore((state) => state.user?.id ?? 'anonymous');
  const canQuickDesensitize = canQuickDesensitizeRole(useAuthStore((state) => state.user?.role));
  // 一键脱敏弹窗的目标项目：单个卡片 / 行传一个，批量传全部勾选项；只有批量提交后移出勾选。
  const [quickTargets, setQuickTargets] = useState<{
    projects: ProjectSummary[];
    batch: boolean;
  } | null>(null);
  const viewKey = `na-landing-project-view:${userId}`;
  const [view, setView] = useState<'grid' | 'list'>(() => {
    try {
      return localStorage.getItem(viewKey) === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });
  // 搜索 / 筛选 / 排序 / 页码存在 URL query：进详情再返回、刷新都保留。
  // 默认只看使用中；已归档项目通过状态筛选找回，不与日常工作项混排。
  const [listQuery, updateListQuery] = useLandingProjectListQuery();
  const { search, status, product, language, desensitization, sort, page } = listQuery;
  // 搜索框用本地草稿即时受控（路由在 transition 里提交 URL，直接绑 URL 会让光标跳动、打断中文输入法），
  // 同步写 URL；URL 被外部改动（返回、重置）时才回灌草稿。
  const [searchDraft, setSearchDraft] = useState(search);
  const lastWrittenSearch = useRef(search);
  useEffect(() => {
    if (search !== lastWrittenSearch.current) {
      lastWrittenSearch.current = search;
      setSearchDraft(search);
    }
  }, [search]);
  const setSearch = (next: string) => {
    lastWrittenSearch.current = next;
    setSearchDraft(next);
    updateListQuery({ search: next });
  };
  const setStatus = (next: string[]) => updateListQuery({ status: next });
  const setProduct = (next: string[]) => updateListQuery({ product: next });
  const setLanguage = (next: string[]) => updateListQuery({ language: next });
  const setDesensitization = (next: string[]) =>
    updateListQuery({
      desensitization: next.filter((value): value is ProjectDesensitizationFilter =>
        DESENSITIZATION_FILTER_OPTIONS.some((option) => option.value === value),
      ),
    });
  const setSort = (next: ProjectSort) => updateListQuery({ sort: next });
  const setPage = (next: number) => updateListQuery({ page: next });
  const products = useLandingProductsPage({ page: 1, pageSize: 200 });
  // 项目语言 = 所选书籍语言版本；候选取完整系统语言集（enabled 只管 App 可选，不代表无存量书）。
  const languages = useLandingProjectLanguageOptions();
  // 跨分页保留勾选行快照及其所来自的列表数据时间；只用更新的行回写，批量归档 / 恢复按已见到的最新状态分组。
  const [selected, setSelected] = useState(new Map<string, SelectedProject>());
  // 批量归档 / 恢复的确认目标在点击时冻结：确认框打开期间后台刷新不替换或清空目标。
  const [bulk, setBulk] = useState<{
    status: 'ARCHIVED' | 'ACTIVE';
    rows: ProjectSummary[];
  } | null>(null);
  const [creating, setCreating] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const queueCount = useLandingProjectExecutionQueueCount();
  const client = useQueryClient();
  const query = useLandingProjects({
    page,
    limit: 20,
    search,
    status: status[0],
    sort,
    product_id: product[0],
    language: language[0],
    desensitization_status: desensitization[0],
  });
  const rows = query.data?.data ?? [];
  // 归档 / 恢复会让当前页变空：页码超出总页数时收回最后一页，避免把越界的空页当成「没有项目」。
  const lastPage = Math.max(1, Math.ceil((query.data?.total ?? 0) / 20));
  useEffect(() => {
    if (query.data && page > lastPage) updateListQuery({ page: lastPage });
  }, [query.data, page, lastPage, updateListQuery]);
  useEffect(() => {
    const latest = query.data?.data;
    if (!latest?.length) return;
    const at = query.dataUpdatedAt;
    const updates: SelectedProject[] = [];
    for (const row of latest) {
      const entry = selected.get(row.id);
      if (!entry) continue;
      const candidate = { row, at };
      // 同一行再次确认只推进数据时间；不同的行只在更新时回写，切回其他筛选 / 分页的旧缓存时快照不倒退。
      if (entry.row === row ? at > entry.at : isNewerSelectedProject(candidate, entry))
        updates.push(candidate);
    }
    if (!updates.length) return;
    setSelected((current) => {
      const next = new Map(current);
      for (const update of updates) if (next.has(update.row.id)) next.set(update.row.id, update);
      return next;
    });
  }, [query.data, query.dataUpdatedAt, selected]);
  const toggle = (row: ProjectSummary) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, { row, at: query.dataUpdatedAt });
      return next;
    });
  const selectedRows = [...selected.values()].map((entry) => entry.row);
  // 批量一键脱敏不按列表统计预判（ADM-LP-S010 / AE §11.1.2）：勾选可跨页、统计可能已过期，
  // 只在勾选项均已归档、书籍不可处理或未加入章节时不可用，其余交给弹窗读最新章节判定。
  const batchQuickDesensitizable = selectedRows.some(
    (row) => quickDesensitizeProjectBlockedReason(row) === null,
  );
  const quickButton = (row: ProjectSummary) => {
    const blocked = canQuickDesensitize
      ? quickDesensitizeBlockedReason(row)
      : 'AI 处理需要运营员及以上权限';
    return (
      <button
        type="button"
        className="btn btn-sm"
        disabled={blocked !== null}
        title={blocked ?? '对未开始或失败的已投放章节提交 AI 脱敏'}
        onClick={() => setQuickTargets({ projects: [row], batch: false })}
      >
        一键脱敏
      </button>
    );
  };
  const archivable = selectedRows.filter((row) => row.status !== 'ARCHIVED');
  const restorable = selectedRows.filter((row) => row.status === 'ARCHIVED');
  // 无批量端点：逐个 PATCH（各自落审计），成功项移出勾选，失败项保留供重试。
  const bulkUpdate = useMutation({
    mutationFn: async (input: { status: 'ARCHIVED' | 'ACTIVE'; rows: ProjectSummary[] }) => {
      const results = await Promise.allSettled(
        input.rows.map((row) => landingProjectsApi.update(row.id, { status: input.status })),
      );
      return input.rows.map((row, index) => ({ row, ok: results[index]?.status === 'fulfilled' }));
    },
    onSuccess: (results, input) => {
      const succeeded = results.filter((result) => result.ok).map((result) => result.row.id);
      const failed = results.length - succeeded.length;
      setSelected((current) => {
        const next = new Map(current);
        for (const id of succeeded) next.delete(id);
        return next;
      });
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      const action = input.status === 'ARCHIVED' ? '归档' : '恢复';
      if (failed)
        toast(`已${action} ${succeeded.length} 个项目，${failed} 个失败，仍保留勾选可重试`, {
          tone: 'warn',
        });
      else toast(`已${action} ${succeeded.length} 个项目`, { tone: 'pos' });
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const hasQuery =
    search.trim() !== '' || product.length > 0 || language.length > 0 || desensitization.length > 0;
  const resetQuery = () => {
    setSearch('');
    setProduct([]);
    setLanguage([]);
    setDesensitization([]);
    setStatus(['ACTIVE']);
    setPage(1);
  };
  const chooseView = (next: 'grid' | 'list') => {
    setView(next);
    try {
      localStorage.setItem(viewKey, next);
    } catch {
      /* 浏览器禁用存储时保留本页视图。 */
    }
  };
  return (
    <div className="vstack" style={{ gap: 12, marginTop: 14 }}>
      <LandingProjectBookVisibilityWarnings onOpen={onOpen} />
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <SearchInput
          value={searchDraft}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          onClear={() => {
            setSearch('');
            setPage(1);
          }}
          placeholder="搜索项目 / 书名 / 书籍编码 / 来源 ID"
          width={360}
        />
        <FilterButton
          label="状态"
          options={[
            { value: 'ACTIVE', label: '使用中' },
            { value: 'ARCHIVED', label: '已归档' },
          ]}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
          multi={false}
        />
        <FilterButton
          label="投放产品"
          options={products.data.map((row) => ({ value: row.id, label: row.name }))}
          value={product}
          multi={false}
          searchable
          onChange={(next) => {
            setProduct(next);
            setPage(1);
          }}
          emptyLabel={
            products.isLoading
              ? '加载投放产品…'
              : products.isError
                ? '投放产品加载失败'
                : '暂无投放产品'
          }
        />
        <FilterButton
          label="语言"
          options={(languages.data ?? []).map((row) => ({
            value: row.language_code,
            label: `${row.language_code.toUpperCase()} ${row.display_name_i18n.zh}`,
          }))}
          value={language}
          multi={false}
          searchable
          width={180}
          onChange={(next) => {
            setLanguage(next);
            setPage(1);
          }}
          emptyLabel={
            languages.isPending ? '加载语言…' : languages.isError ? '语言加载失败' : '暂无语言'
          }
        />
        <FilterButton
          label="AI 脱敏"
          options={DESENSITIZATION_FILTER_OPTIONS}
          value={desensitization}
          multi={false}
          onChange={(next) => {
            setDesensitization(next);
            setPage(1);
          }}
        />
        <FilterButton
          label="排序"
          multi={false}
          options={[
            { value: 'updated_desc', label: '最近更新' },
            { value: 'created_desc', label: '最近创建' },
            { value: 'name_asc', label: '项目名称' },
          ]}
          value={[sort]}
          onChange={(next) => {
            const value = next[0];
            if (value === 'updated_desc' || value === 'created_desc' || value === 'name_asc') {
              setSort(value);
              setPage(1);
            }
          }}
        />
        <div className="seg landing-project-view-switch">
          <button
            type="button"
            className={view === 'grid' ? 'on' : ''}
            aria-pressed={view === 'grid'}
            onClick={() => chooseView('grid')}
          >
            <Icons.IconLayers size={12} /> 卡片
          </button>
          <button
            type="button"
            className={view === 'list' ? 'on' : ''}
            aria-pressed={view === 'list'}
            onClick={() => chooseView('list')}
          >
            <Icons.IconList size={12} /> 列表
          </button>
        </div>
        {selected.size > 0 && (
          <>
            <span className="muted">已选 {selected.size} 个项目</span>
            {canQuickDesensitize && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={!batchQuickDesensitizable}
                title={
                  batchQuickDesensitizable
                    ? '逐项目读取最新章节，确认后提交可处理的章节'
                    : '勾选项均已归档、书籍不可处理或未加入投放章节'
                }
                // 弹窗列出全部勾选项：不可用的项目只展示原因、不提交（ADM-LP-S010）。
                onClick={() => setQuickTargets({ projects: selectedRows, batch: true })}
              >
                批量一键脱敏
              </button>
            )}
            {archivable.length > 0 && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={bulkUpdate.isPending}
                onClick={() => setBulk({ status: 'ARCHIVED', rows: archivable })}
              >
                归档 {archivable.length} 个
              </button>
            )}
            {restorable.length > 0 && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={bulkUpdate.isPending}
                onClick={() => setBulk({ status: 'ACTIVE', rows: restorable })}
              >
                恢复 {restorable.length} 个
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={bulkUpdate.isPending}
              onClick={() => setSelected(new Map())}
            >
              清空选择
            </button>
          </>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn"
          aria-label={`执行队列，进行中 ${queueCount.data ?? 0} 批`}
          onClick={() => setQueueOpen(true)}
        >
          <Icons.IconClock size={12} />
          执行队列
          {queueCount.data ? <span className="mono"> · {queueCount.data}</span> : null}
        </button>
        <button type="button" className="btn btn-accent" onClick={() => setCreating(true)}>
          <Icons.IconPlus size={12} />
          新建项目
        </button>
      </div>
      {query.isPending ? (
        view === 'grid' ? (
          <CardGridSkeleton count={6} minWidth={320} />
        ) : (
          <TableSkeleton rows={8} cols={7} />
        )
      ) : query.isError ? (
        <div className="card landing-project-state" role="alert">
          <Icons.IconAlert size={18} style={{ color: 'var(--neg)' }} />
          <span className="landing-project-state-message">项目加载失败：{query.error.message}</span>
          <button type="button" className="btn" onClick={() => void query.refetch()}>
            重新加载
          </button>
        </div>
      ) : !rows.length ? (
        <div className="card landing-project-state">
          <Icons.IconLayers size={18} style={{ color: 'var(--fg-faint)' }} />
          <span className="muted">
            {hasQuery
              ? '没有符合当前搜索或筛选条件的项目。'
              : status[0] === 'ARCHIVED'
                ? '没有已归档的项目。'
                : status[0] === 'ACTIVE'
                  ? '暂无使用中的投放项目。新建项目后可选择章节、上传素材并生成 HTML。'
                  : '还没有投放项目。新建项目后可选择章节、上传素材并生成 HTML。'}
          </span>
          {hasQuery || status[0] === 'ARCHIVED' ? (
            <button type="button" className="btn" onClick={resetQuery}>
              {hasQuery ? '清除搜索与筛选' : '查看使用中项目'}
            </button>
          ) : (
            <button type="button" className="btn btn-accent" onClick={() => setCreating(true)}>
              <Icons.IconPlus size={12} />
              新建项目
            </button>
          )}
        </div>
      ) : view === 'grid' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,320px),1fr))',
            gap: 12,
          }}
        >
          {rows.map((row) => (
            <article
              className="card"
              key={row.id}
              style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <div className="hstack" style={{ gap: 8 }}>
                <input
                  type="checkbox"
                  aria-label={`选择项目 ${row.name}`}
                  checked={selected.has(row.id)}
                  onChange={() => toggle(row)}
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{
                    padding: 0,
                    height: 'auto',
                    minHeight: 'var(--btn-h)',
                    minWidth: 0,
                    display: 'block',
                    textAlign: 'left',
                    flex: 1,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  }}
                  onClick={() => onOpen(row.id)}
                >
                  {row.name}
                </button>
                <ProjectState status={row.status} />
              </div>
              <BookIdentity row={row} />
              <div style={{ overflowWrap: 'anywhere' }}>
                {row.product.name} · {row.media_source}
              </div>
              <div className="mono muted">
                AI 审核 {row.chapters.reviewed ?? '—'}/{row.chapters.selected} · AI 脱敏{' '}
                {row.chapters.desensitized ?? '—'}/{row.chapters.selected} · HTML 就绪{' '}
                {row.chapters.ready}/{row.chapters.selected} · 素材 {row.media_count} · HTML{' '}
                {row.tasks.generated}/{row.tasks.total}
              </div>
              {row.tasks.stale > 0 && (
                <span className="chip chip-warn landing-project-notice">
                  {row.tasks.stale} 个 HTML 落地页需重新生成并重新下载
                </span>
              )}
              {row.active_execution && <ProjectProgressSummary execution={row.active_execution} />}
              {row.error_summary && !errorSummaryShownInProgress(row) && (
                <p
                  role="alert"
                  style={{ color: 'var(--neg)', overflowWrap: 'anywhere', margin: 0 }}
                >
                  {row.error_summary}
                </p>
              )}
              <div className="hstack" style={{ gap: 8, marginTop: 'auto' }}>
                <CopyableCode value={row.code} label="项目编号" />
                <div style={{ flex: 1 }} />
                {quickButton(row)}
                <button type="button" className="btn btn-sm" onClick={() => onOpen(row.id)}>
                  打开项目
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="card">
          <Table className="landing-content-table" style={{ minWidth: 1320, tableLayout: 'fixed' }}>
            <colgroup>
              {[40, 240, 260, 160, 120, 240, 150, 110].map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="全选当前页项目"
                    checked={rows.length > 0 && rows.every((row) => selected.has(row.id))}
                    onChange={(event) =>
                      setSelected((current) => {
                        const next = new Map(current);
                        // 全选只补加未勾选的项，不用当前页（可能是旧缓存）的行覆盖已有快照。
                        for (const row of rows)
                          if (!event.target.checked) next.delete(row.id);
                          else if (!next.has(row.id))
                            next.set(row.id, { row, at: query.dataUpdatedAt });
                        return next;
                      })
                    }
                  />
                </th>
                <th>项目</th>
                <th>书籍</th>
                <th>产品 / 渠道</th>
                <th>章节 / 任务</th>
                <th>进度与结果</th>
                <th>更新</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`选择项目 ${row.name}`}
                      checked={selected.has(row.id)}
                      onChange={() => toggle(row)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{
                        whiteSpace: 'normal',
                        height: 'auto',
                        minHeight: 'var(--btn-h)',
                        textAlign: 'left',
                      }}
                      onClick={() => onOpen(row.id)}
                    >
                      {row.name}
                    </button>
                    <CopyableCode value={row.code} label="项目编号" />
                  </td>
                  <td>
                    <BookIdentity row={row} />
                  </td>
                  <td style={{ overflowWrap: 'anywhere' }}>
                    {row.product.name}
                    <div className="mono muted">{row.media_source}</div>
                  </td>
                  <td className="mono">
                    AI 审核 {row.chapters.reviewed ?? '—'}/{row.chapters.selected}
                    <div>
                      AI 脱敏 {row.chapters.desensitized ?? '—'}/{row.chapters.selected}
                    </div>
                    <div>
                      HTML 就绪 {row.chapters.ready}/{row.chapters.selected}
                    </div>
                    <div>
                      {row.tasks.generated}/{row.tasks.total} HTML
                    </div>
                    {row.tasks.stale > 0 && (
                      <span className="chip chip-warn landing-project-notice">
                        {row.tasks.stale} 待更新
                      </span>
                    )}
                  </td>
                  <td>
                    {row.active_execution ? (
                      <ProjectProgressSummary execution={row.active_execution} />
                    ) : (
                      <ProjectState status={row.status} />
                    )}
                    {row.error_summary && !errorSummaryShownInProgress(row) && (
                      <p role="alert" style={{ color: 'var(--neg)' }}>
                        {row.error_summary}
                      </p>
                    )}
                  </td>
                  <td className="mono muted">{fmtDateTime(row.updated_at)}</td>
                  <td>
                    <div className="vstack" style={{ gap: 6, alignItems: 'flex-start' }}>
                      <button type="button" className="btn btn-sm" onClick={() => onOpen(row.id)}>
                        打开项目
                      </button>
                      {quickButton(row)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <Pagination total={query.data?.total ?? 0} page={page} pageSize={20} onChange={setPage} />
      {bulk && (
        <ConfirmDialog
          title={bulk.status === 'ARCHIVED' ? '批量归档项目？' : '批量恢复项目？'}
          message={
            bulk.status === 'ARCHIVED'
              ? `将归档 ${bulk.rows.length} 个项目，它们不再出现在默认的使用中列表。`
              : `将恢复 ${bulk.rows.length} 个已归档项目为使用中。`
          }
          details={[
            ...bulk.rows.slice(0, 5).map((row) => row.name),
            ...(bulk.rows.length > 5 ? [`等共 ${bulk.rows.length} 个`] : []),
            '任务、HTML 产物与章节处理记录全部保留。',
          ]}
          tone={bulk.status === 'ARCHIVED' ? 'warn' : 'default'}
          confirmLabel={bulk.status === 'ARCHIVED' ? '归档' : '恢复'}
          onConfirm={() => bulkUpdate.mutate(bulk)}
          onClose={() => setBulk(null)}
        />
      )}
      {creating && (
        <LandingProjectEditor
          onClose={() => setCreating(false)}
          onSaved={(result) => {
            setCreating(false);
            onOpen(result.project.id);
          }}
        />
      )}
      {queueOpen && <LandingExecutionQueueDrawer onClose={() => setQueueOpen(false)} />}
      {quickTargets && (
        <LandingProjectQuickDesensitizeDialog
          projects={quickTargets.projects}
          onClose={() => setQuickTargets(null)}
          onSubmitted={
            quickTargets.batch
              ? (ids) =>
                  setSelected((current) => {
                    const next = new Map(current);
                    for (const id of ids) next.delete(id);
                    return next;
                  })
              : undefined
          }
        />
      )}
    </div>
  );
}
